/**
 * api.ts — Centralized Axios client for all Blink Relay backend calls.
 *
 * Architecture note: this module has zero MSAL/auth imports by design.
 * The token getter is injected at runtime by useAuth (via setTokenGetter),
 * which keeps this file independently testable and free of browser-auth
 * side-effects.
 *
 * API surface is grouped into five namespaces:
 *   authApi     — current user identity (/api/v1/auth/me)
 *   requestsApi — CRUD + listing for BlinkRequest records
 *   workflowApi — status-transition endpoints (approve, reject, updateStatus)
 *   threadApi   — per-request message thread + clarification flow
 *   filesApi    — attachment upload / listing
 *   respondApi  — public (unauthenticated) response endpoint for requestors
 */

import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from "axios";
import type {
  ApprovePayload,
  ApproveResult,
  Attachment,
  BlinkRequest,
  ClarifyPayload,
  Message,
  MessageCreate,
  RejectPayload,
  ClassificationPayload,
  RequestTicket,
  RequestTickets,
  Reviewer,
  TicketCreatePayload,
  RequestCreate,
  RequestFilters,
  RequestListResponse,
  RequestUpdate,
  RespondPayload,
  SimilarRequest,
  StatusUpdatePayload,
  TimelineEvent,
  User,
} from "./types";

// ── Centralized API Endpoint Routes ───────────────────────────────────────────
/**
 * Centralized API endpoint routes.
 * Using a constant ensures all route changes are made in one place,
 * preventing typos and making it easier to refactor endpoints.
 */
const API_ROUTES = {
  AUTH: "/auth/me",
  REQUESTS: "/requests",
  REQUESTS_MINE: "/requests/mine",
  REQUEST_DETAIL: (id: string) => `/requests/${id}`,
  REQUEST_SIMILAR: (id: string) => `/requests/${id}/similar`,
  REQUEST_TIMELINE: (id: string) => `/requests/${id}/timeline`,
  REQUEST_STATUS: (id: string) => `/requests/${id}/status`,
  REQUEST_APPROVE: (id: string) => `/requests/${id}/approve`,
  REQUEST_REJECT: (id: string) => `/requests/${id}/reject`,
  REQUEST_CLASSIFICATION: (id: string) => `/requests/${id}/classification`,
  REQUEST_TICKETS: (id: string) => `/requests/${id}/tickets`,
  REQUEST_CLAIM: (id: string) => `/requests/${id}/claim`,
  REQUEST_UNCLAIM: (id: string) => `/requests/${id}/unclaim`,
  REQUEST_CANCEL: (id: string) => `/requests/${id}/cancel`,
  REQUEST_RESUBMIT: (id: string) => `/requests/${id}/resubmit`,
  REQUEST_EXPORT: "/requests/export",
  REQUEST_MESSAGES: (id: string) => `/requests/${id}/messages`,
  REQUEST_MESSAGE_READ: (id: string, msgId: string) =>
    `/requests/${id}/messages/${msgId}/read`,
  REQUEST_STREAM: (id: string) => `/requests/${id}/stream`,
  REQUEST_WATCHERS: (id: string) => `/requests/${id}/watchers`,
  REQUEST_CLARIFY: (id: string) => `/requests/${id}/clarify`,
  REQUEST_FILES: (id: string) => `/requests/${id}/files`,
  REQUEST_FILE_CONTENT: (id: string, fileId: string) =>
    `/requests/${id}/files/${fileId}/content`,
  REQUEST_FILE_DELETE: (id: string, fileId: string) =>
    `/requests/${id}/files/${fileId}`,
  ESCALATIONS_SUMMARY: "/requests/escalations/summary",
  USERS: "/users",
  RESPOND: (id: string) => `/requests/${id}/respond`,
} as const;

export type ApiRoute = typeof API_ROUTES;

// ── Token injection ───────────────────────────────────────────────────────────

// Token getter is injected by useAuth so api.ts has no direct MSAL dependency.
// In SKIP_AUTH mode useAuth injects a static dev token instead.
let _tokenGetter: (() => Promise<string | null>) | null = null;

/** Called once by useAuth on mount to wire up Bearer-token acquisition. */
export function setTokenGetter(getter: () => Promise<string | null>): void {
  _tokenGetter = getter;
}

/** Returns the current auth token. Used by SSE hook (fetch-based, not Axios). */
export async function getAuthToken(): Promise<string | null> {
  return _tokenGetter ? _tokenGetter() : null;
}

// ── Axios instance ────────────────────────────────────────────────────────────

const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL
    ? `${import.meta.env.VITE_API_BASE_URL}/api/v1`
    : "/api/v1",
  headers: { "Content-Type": "application/json" },
  timeout: 30_000,
  withCredentials: true, // send relay_session cookie on every request
});

// Store CSRF token from response headers
let _csrfToken: string | null = null;

// ── Session expired callback ───────────────────────────────────────────────────
// Injected by useAuth so api.ts stays free of direct auth dependencies.
// Fired at most once per page lifetime (guard prevents concurrent 401 races).
let _sessionExpiredHandler: (() => void) | null = null;
let _sessionExpiredFired = false;

/** Called by useAuth to wire up the 401 → logout redirect. */
export function setSessionExpiredHandler(handler: (() => void) | null): void {
  _sessionExpiredHandler = handler;
  _sessionExpiredFired = false;
}

// Attach the Bearer token to every request via the injected getter.
// acquireTokenSilent will refresh the MSAL token automatically; if it can't,
// it falls back to a redirect so the user re-authenticates.
apiClient.interceptors.request.use(async (config) => {
  if (_tokenGetter) {
    const token = await _tokenGetter();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }

  // Attach CSRF token for state-changing requests
  if (
    _csrfToken &&
    ["POST", "PUT", "DELETE", "PATCH"].includes(
      config.method?.toUpperCase() || "",
    )
  ) {
    config.headers["X-CSRF-Token"] = _csrfToken;
  }

  return config;
});

apiClient.interceptors.response.use(
  (res) => {
    // Capture CSRF token from response header
    const csrfToken = res.headers["x-csrf-token"];
    if (csrfToken) {
      _csrfToken = csrfToken;
    }
    return res;
  },
  async (error) => {
    // A CSRF rejection carries a fresh token: capture it and retry the request
    // once. Tokens expire after 30 minutes, so this covers forms left open
    // longer than that (e.g. a long request description) without the user
    // seeing "Invalid CSRF token".
    const freshCsrf = error.response?.headers?.["x-csrf-token"];
    if (freshCsrf) {
      _csrfToken = freshCsrf;
    }
    const original = error.config as
      (InternalAxiosRequestConfig & { _csrfRetried?: boolean }) | undefined;
    const detail: unknown = error.response?.data?.detail;
    const isCsrfRejection =
      error.response?.status === 403 &&
      typeof detail === "string" &&
      /^(Invalid )?CSRF token/.test(detail);
    if (isCsrfRejection && original && !original._csrfRetried) {
      original._csrfRetried = true;
      if (!freshCsrf) {
        // Older API without a token on the 403: any successful call mints one.
        await apiClient.get(API_ROUTES.AUTH).catch(() => undefined);
      }
      return apiClient(original);
    }

    if (
      error.response?.status === 401 &&
      _sessionExpiredHandler &&
      !_sessionExpiredFired
    ) {
      _sessionExpiredFired = true;
      _sessionExpiredHandler();
    }

    // Normalize FastAPI's `detail` field and generic messages into a single
    // Error so callers only need to handle one shape.
    const message: string =
      error.response?.data?.detail ??
      error.response?.data?.message ??
      error.message ??
      "An unexpected error occurred";
    return Promise.reject(new Error(message));
  },
);

// ── Auth ──────────────────────────────────────────────────────────────────────

export const authApi = {
  me: (): Promise<User> =>
    apiClient.get<User>(API_ROUTES.AUTH).then((r) => r.data),

  // Upserts user in DB and returns DB-assigned roles (not just JWT claims).
  // Call this for MSAL users so DB-managed roles override the default REQUESTOR
  // fallback that applies when Azure AD app roles are not yet configured.
  sync: (): Promise<User> =>
    apiClient.post<User>("/auth/sync", {}).then((r) => r.data),
};

// ── Requests ──────────────────────────────────────────────────────────────────

export const requestsApi = {
  create: (payload: RequestCreate): Promise<BlinkRequest> =>
    apiClient
      .post<BlinkRequest>(API_ROUTES.REQUESTS, payload)
      .then((r) => r.data),

  /**
   * Fetch a paginated list of all requests (reviewer/PM view).
   * Only scalar filters are serialized here — multi-value filtering
   * (status[], pod[], priority[], type[]) is handled client-side in
   * DashboardPage after fetching page_size=500.
   */
  list: (filters: RequestFilters = {}): Promise<RequestListResponse> => {
    const params: Record<string, string | number> = {};
    if (filters.pod) params.pod = filters.pod;
    if (filters.status) params.status = filters.status;
    if (filters.request_type) params.request_type = filters.request_type;
    if (filters.priority) params.priority = filters.priority;
    if (filters.search) params.search = filters.search;
    if (filters.page) params.page = filters.page;
    if (filters.page_size) params.page_size = filters.page_size;
    return apiClient
      .get<RequestListResponse>(API_ROUTES.REQUESTS, { params })
      .then((r) => r.data);
  },

  /** Requestor-scoped list — only returns requests owned by the current user. */
  listMine: (filters: RequestFilters = {}): Promise<RequestListResponse> => {
    const params: Record<string, string | number> = {};
    if (filters.status) params.status = filters.status;
    if (filters.request_type) params.request_type = filters.request_type;
    if (filters.priority) params.priority = filters.priority;
    if (filters.search) params.search = filters.search;
    if (filters.page) params.page = filters.page;
    if (filters.page_size) params.page_size = filters.page_size;
    return apiClient
      .get<RequestListResponse>(API_ROUTES.REQUESTS_MINE, { params })
      .then((r) => r.data);
  },

  /**
   * Server-side CSV export endpoint — used as a fallback / alternative.
   * Note: DashboardPage generates its CSV client-side from the already-filtered
   * array so that multi-select filters (which can't be passed as single query
   * params) are correctly reflected in the download.
   */
  exportCsv: (
    filters: Omit<RequestFilters, "page" | "page_size"> = {},
  ): Promise<Blob> => {
    const params: Record<string, string> = {};
    if (filters.pod) params.pod = filters.pod;
    if (filters.status) params.status = filters.status;
    if (filters.request_type) params.request_type = filters.request_type;
    if (filters.priority) params.priority = filters.priority;
    if (filters.search) params.search = filters.search;
    return apiClient
      .get(API_ROUTES.REQUEST_EXPORT, { params, responseType: "blob" })
      .then((r) => r.data as Blob);
  },

  get: (id: string): Promise<BlinkRequest> =>
    apiClient
      .get<BlinkRequest>(API_ROUTES.REQUEST_DETAIL(id))
      .then((r) => r.data),

  getSimilar: (id: string, limit: number = 5): Promise<SimilarRequest[]> =>
    apiClient
      .get<SimilarRequest[]>(API_ROUTES.REQUEST_SIMILAR(id), {
        params: { limit },
      })
      .then((r) => r.data),

  getTimeline: (id: string): Promise<TimelineEvent[]> =>
    apiClient
      .get<TimelineEvent[]>(API_ROUTES.REQUEST_TIMELINE(id))
      .then((r) => r.data),

  getWatchers: (id: string): Promise<{ count: number; names: string[] }> =>
    apiClient
      .get<{ count: number; names: string[] }>(API_ROUTES.REQUEST_WATCHERS(id))
      .then((r) => r.data),

  getEscalationSummary: (): Promise<{
    total: number;
    by_pod: Record<string, number>;
    by_priority: Record<string, number>;
    oldest_days: number | null;
  }> => apiClient.get(API_ROUTES.ESCALATIONS_SUMMARY).then((r) => r.data),

  update: (id: string, payload: RequestUpdate): Promise<BlinkRequest> =>
    apiClient
      .patch<BlinkRequest>(API_ROUTES.REQUEST_DETAIL(id), payload)
      .then((r) => r.data),

  resubmit: (id: string, payload: RequestUpdate): Promise<BlinkRequest> =>
    apiClient
      .post<BlinkRequest>(API_ROUTES.REQUEST_RESUBMIT(id), payload)
      .then((r) => r.data),
};

// ── Workflow ──────────────────────────────────────────────────────────────────
// Dedicated endpoints for status transitions so the backend can apply
// validation rules, trigger email notifications, and create JSM/Jira
// tickets as side-effects — PATCH /status is for generic moves, while
// /approve and /reject have their own endpoints for richer payloads.

export const workflowApi = {
  updateStatus: (
    id: string,
    payload: StatusUpdatePayload,
  ): Promise<{ id: string; status: string }> =>
    apiClient.patch(API_ROUTES.REQUEST_STATUS(id), payload).then((r) => r.data),

  approve: (id: string, payload: ApprovePayload = {}): Promise<ApproveResult> =>
    apiClient.post(API_ROUTES.REQUEST_APPROVE(id), payload).then((r) => r.data),

  /** Uses /reject (not /status) so the backend can fire the rejection email notification. */
  reject: (
    id: string,
    payload: RejectPayload,
  ): Promise<{ id: string; status: string }> =>
    apiClient.post(API_ROUTES.REQUEST_REJECT(id), payload).then((r) => r.data),

  /** Change type and/or pod before approval (Pod Reviewer, PM or Admin). */
  updateClassification: (
    id: string,
    payload: ClassificationPayload,
  ): Promise<{ id: string; request_type: string; pod: string }> =>
    apiClient
      .post(API_ROUTES.REQUEST_CLASSIFICATION(id), payload)
      .then((r) => r.data),

  /** Claim a request — mark that this PM is working on it. */
  claimRequest: (
    id: string,
  ): Promise<{ id: string; claimed_by: string; claimed_at: string }> =>
    apiClient.post(API_ROUTES.REQUEST_CLAIM(id), {}).then((r) => r.data),

  /** Unclaim a request — release it so others can work on it. */
  unclaimRequest: (
    id: string,
  ): Promise<{ id: string; claimed_by: null; message: string }> =>
    apiClient.post(API_ROUTES.REQUEST_UNCLAIM(id), {}).then((r) => r.data),

  /** Cancel a request — only available for requestors on their own requests. */
  cancel: (id: string): Promise<{ id: string; status: string }> =>
    apiClient.post(API_ROUTES.REQUEST_CANCEL(id), {}).then((r) => r.data),
};

// ── Thread ────────────────────────────────────────────────────────────────────

export const threadApi = {
  /** Oldest first. With `limit`, only the latest page (or the page before `before`). */
  list: (
    requestId: string,
    page: { limit?: number; before?: string } = {},
  ): Promise<Message[]> =>
    apiClient
      .get<Message[]>(API_ROUTES.REQUEST_MESSAGES(requestId), { params: page })
      .then((r) => r.data),

  post: (requestId: string, payload: MessageCreate): Promise<Message> =>
    apiClient
      .post<Message>(API_ROUTES.REQUEST_MESSAGES(requestId), payload)
      .then((r) => r.data),

  /** Comment with screenshots: text and images go in one multipart request. */
  postWithImages: (
    requestId: string,
    payload: MessageCreate,
    images: File[],
  ): Promise<Message> => {
    const formData = new FormData();
    formData.append("body", payload.body);
    formData.append("is_internal", String(payload.is_internal ?? false));
    formData.append("mentions", JSON.stringify(payload.mentions ?? []));
    if (payload.reply_to_id)
      formData.append("reply_to_id", payload.reply_to_id);
    for (const image of images) {
      formData.append("files", image);
    }
    // Remove the JSON default so the browser sets the multipart boundary.
    return apiClient
      .post<Message>(
        `${API_ROUTES.REQUEST_MESSAGES(requestId)}/with-images`,
        formData,
        { headers: { "Content-Type": null } },
      )
      .then((r) => r.data);
  },

  clarify: (requestId: string, payload: ClarifyPayload): Promise<Message> =>
    apiClient
      .post<Message>(API_ROUTES.REQUEST_CLARIFY(requestId), payload)
      .then((r) => r.data),

  markRead: (requestId: string, messageId: string): Promise<void> =>
    apiClient
      .post(API_ROUTES.REQUEST_MESSAGE_READ(requestId, messageId))
      .then(() => undefined),
};

// ── Files ─────────────────────────────────────────────────────────────────────

export const filesApi = {
  list: (requestId: string): Promise<Attachment[]> =>
    apiClient
      .get<Attachment[]>(API_ROUTES.REQUEST_FILES(requestId))
      .then((r) => r.data),

  upload: (requestId: string, file: File): Promise<Attachment> =>
    filesApi.uploadMany(requestId, [file]).then((arr) => arr[0]),

  content: (requestId: string, fileId: string): Promise<string> =>
    apiClient
      .get<string>(API_ROUTES.REQUEST_FILE_CONTENT(requestId, fileId), {
        responseType: "text",
        // Keep CSV/text verbatim; axios would otherwise try to JSON-parse it.
        transformResponse: (d) => d,
      })
      .then((r) => r.data),

  /** Fetch a file through the API (signed-in), e.g. for a full-size view.
   *  Unlike the short-lived download link, this never expires. */
  blob: (requestId: string, fileId: string): Promise<Blob> =>
    apiClient
      .get<Blob>(API_ROUTES.REQUEST_FILE_CONTENT(requestId, fileId), {
        responseType: "blob",
      })
      .then((r) => r.data),

  uploadMany: (requestId: string, files: File[]): Promise<Attachment[]> => {
    const formData = new FormData();
    // Backend expects a list under the field name "files" (multipart spec
    // for repeated fields → list[UploadFile])
    for (const file of files) {
      formData.append("files", file);
    }
    // Pass Content-Type: null to remove the instance default "application/json".
    // The browser then sets "multipart/form-data; boundary=..." automatically
    // when it serialises the FormData body. Without this, the instance default
    // wins and FastAPI rejects the upload with 422.
    return apiClient
      .post<Attachment[]>(API_ROUTES.REQUEST_FILES(requestId), formData, {
        headers: { "Content-Type": null },
      })
      .then((r) => r.data);
  },

  // FEATURE: Allow requestors to delete incorrectly uploaded attachments
  delete: (requestId: string, attachmentId: string): Promise<void> =>
    apiClient
      .delete(API_ROUTES.REQUEST_FILE_DELETE(requestId, attachmentId))
      .then(() => undefined),
};

// ── Users ─────────────────────────────────────────────────────────────────────

export const usersApi = {
  list: (
    q?: string,
  ): Promise<Array<{ oid: string; email: string; display_name: string }>> => {
    const params: Record<string, string> = {};
    if (q) params.q = q;
    return apiClient.get(API_ROUTES.USERS, { params }).then((r) => r.data);
  },
};
// ── Respond (public — no auth required) ──────────────────────────────────────
// Requestors follow an emailed link to /respond/:id without logging in.
// The backend validates the request belongs to their email; no Bearer token needed.

export const respondApi = {
  respond: (
    requestId: string,
    payload: RespondPayload,
  ): Promise<{ id: string; status: string }> =>
    apiClient.post(API_ROUTES.RESPOND(requestId), payload).then((r) => r.data),
};

// ── Health ────────────────────────────────────────────────────────────────────

export const healthApi = {
  detailed: () => apiClient.get("/health/detailed").then((r) => r.data),
};

// ── Admin ─────────────────────────────────────────────────────────────────────

export interface AdminUser {
  id: string;
  oid: string | null;
  email: string;
  display_name: string;
  roles: string[];
}

// ── Jira tickets raised from a request ────────────────────────────────────────

export const ticketsApi = {
  list: (requestId: string): Promise<RequestTickets> =>
    apiClient
      .get<RequestTickets>(API_ROUTES.REQUEST_TICKETS(requestId))
      .then((r) => r.data),

  create: (
    requestId: string,
    payload: TicketCreatePayload,
  ): Promise<RequestTicket> =>
    apiClient
      .post<RequestTicket>(API_ROUTES.REQUEST_TICKETS(requestId), payload)
      .then((r) => r.data),

  /** Detach a ticket from the request (the Jira ticket isn't deleted). */
  unlink: (
    requestId: string,
    issueKey: string,
  ): Promise<{ issue_key: string; unlinked: boolean }> =>
    apiClient
      .delete(
        `${API_ROUTES.REQUEST_TICKETS(requestId)}/${encodeURIComponent(issueKey)}`,
      )
      .then((r) => r.data),

  /** Attach an existing Jira ticket by key or URL. */
  link: (requestId: string, issue: string): Promise<RequestTicket> =>
    apiClient
      .post<RequestTicket>(`${API_ROUTES.REQUEST_TICKETS(requestId)}/link`, {
        issue,
      })
      .then((r) => r.data),
};

// ── Assigning requests to reviewers ───────────────────────────────────────────

export const assignmentApi = {
  reviewers: (): Promise<Reviewer[]> =>
    apiClient.get<Reviewer[]>("/reviewers").then((r) => r.data),

  assign: (
    requestId: string,
    email: string,
  ): Promise<{ id: string; claimed_by: string; claimed_by_email: string }> =>
    apiClient
      .post(`/requests/${requestId}/assign`, { email })
      .then((r) => r.data),
};

export const adminApi = {
  listUsers: (): Promise<AdminUser[]> =>
    apiClient.get("/admin/users").then((r) => r.data),

  updateRoles: (userId: string, roles: string[]): Promise<AdminUser> =>
    apiClient
      .patch(`/admin/users/${userId}/roles`, { roles })
      .then((r) => r.data),
};

export default apiClient;
